import * as React from "react";
import { styled, Grid, SxProps } from "@mui/material";
import PushContentSlide, { PushContentSlideProps } from "./PushContentSlide";

import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperClass } from "swiper/types";

import "swiper/css";

import SliderControl from "../SliderControl";
import PushContentSlideSkeleton from "./PushContentSlide/PushContentSlideSkeleton";

export type PushContentSliderProps = {
  sx?: SxProps;
  loading?: boolean;
  list: (PushContentSlideProps & { id: number })[];
  onChangeSlide?: (index: number) => void;
};

const PushContentSliderWrapper: React.FC<PushContentSliderProps> = (props) => {
  const { list, onChangeSlide, loading = false } = props;
  const swiperRef = React.useRef<SwiperClass | null>(null);
  const nbSlides = React.useMemo(() => list.length - 1, [list]);
  const [swiperControlLeftVisible, setSwiperControlLeftVisible] = React.useState(false);
  const [swiperControlRightVisible, setSwiperControlRightVisible] = React.useState(true);

  const loadingList = Array(3).fill("loading");

  const onSwiper = React.useCallback((swiper: SwiperClass) => {
    swiperRef.current = swiper;
  }, []);

  const onClickPrev = React.useCallback(() => {
    if (!swiperRef.current) return;
    swiperRef.current.slidePrev();
  }, []);

  const onClickNext = React.useCallback(() => {
    if (!swiperRef.current) return;
    swiperRef.current.slideNext();
  }, []);

  const onSlideChange = React.useCallback(
    (swiper: SwiperClass) => {
      setSwiperControlLeftVisible(swiper.activeIndex > 0);
      setSwiperControlRightVisible(swiper.activeIndex < nbSlides);
      onChangeSlide && onChangeSlide(swiper.activeIndex);
    },
    [onChangeSlide, nbSlides]
  );

  if (loading) {
    return (
      <StyledGridSkeleton container spacing={3}>
        {loadingList.map((l, i) => (
          <Grid item xs={12} key={i}>
            <PushContentSlideSkeleton />
          </Grid>
        ))}
      </StyledGridSkeleton>
    );
  }

  return (
    <StyledSwiper>
      <Swiper height={408} onSwiper={onSwiper} onSlideChange={onSlideChange} spaceBetween={24}>
        {list.map((pushContentSlide) => (
          <SwiperSlide key={pushContentSlide.id}>
            <PushContentSlide {...pushContentSlide} className="push-content-slide-item" sx={{ width: "auto" }} />
          </SwiperSlide>
        ))}
        <StyledSliderControl controlType="prev" onClick={onClickPrev} disabled={!swiperControlLeftVisible} />
        <StyledSliderControl controlType="next" onClick={onClickNext} disabled={!swiperControlRightVisible} />
      </Swiper>
    </StyledSwiper>
  );
};

const PushContentSlider = React.memo(PushContentSliderWrapper);

export default PushContentSlider;

const StyledSwiper = styled("div")(({ theme }) => ({
  ".swiper": {
    overflow: "visible",
    height: 468,
  },
  ".swiper-wrapper": {},
  ".swiper-slide": {
    cursor: "pointer",
    ".push-content-slide-item": {
      "&:after": {
        opacity: 1,
      },
      div: {
        opacity: 0,
      },
    },
    "&.swiper-slide-active": {
      ".push-content-slide-item": {
        "&:after": {
          opacity: 0,
        },
        div: {
          opacity: 1,
        },
      },
    },
    "&:hover": {
      ".push-content-slide-item": {
        "&:after": {
          opacity: 0,
        },
      },
    },
  },
}));

const StyledGridSkeleton = styled(Grid)(({ theme }) => ({
  flexWrap: "nowrap",
  ".MuiGrid-root": {
    flexShrink: 0,
  },
}));
const StyledSliderControl = styled(SliderControl)(({ theme }) => ({
  width: 52,
  height: 52,
  "&:first-of-type": {
    left: -16,
  },
  "&:last-of-type": {
    right: -16,
  },
}));
